import {
  GOOGLE_AUTH_OUTCOMES,
  signInWithGoogleCredential,
} from "./googleAuth";
import {
  clearPendingAuthIntent,
  getPendingAuthIntent,
  PENDING_AUTH_INTENT_ACTIONS,
} from "./pendingAuthIntent";
import {
  createLettuceTalkMergeTicket,
  redeemLettuceTalkMergeTicket,
} from "../empathyAI/accountMergeTickets";
import { auth } from "../../configs/firebaseConfig";
import { getAllOwnedCardGenerations } from "../firestore";
import {
  getAccountSessionRecoveryState,
  hasUnfinishedAccountSessionRecovery,
  saveAccountSessionRecoveryState,
  clearAccountMenuIntent,
  clearAccountSessionRecoveryState,
  ACCOUNT_SESSION_RECOVERY_TTL_MS,
} from "./accountSessionRecovery";
import { waitForSettledAuthUser } from "./authState";
import { trackMixpanelEvent } from "../Analytics";

export const ACCOUNT_LINKING_RESULT_OUTCOMES = Object.freeze({
  LINKED: "linked",
  CANCELLED: "cancelled",
  ERROR: "error",
  FALLBACK_CONFLICT: "fallback-conflict",
});

export const ACCOUNT_LINKING_STATUS_MESSAGES = Object.freeze({
  continuationFailure:
    "Google is connected, but we could not continue just now. Please try again in a moment.",
  conflict:
    "That Google account is already connected to LettuceTalk. This card is still here in your current session.",
  missingMergeTarget:
    "We could not confirm that Google account for this transfer. Your card is still here. Please try another Google account.",
  missingMergeSource:
    "Sorry, something went wrong. Please try again in a moment.",
  mergeFailure:
    "We could not move this card to that Google account just now. Your card is still here. Please try again in a moment.",
  mergeSignInCancelled:
    "No changes were made. Your card is still here in this session.",
  mergeSignInFailure:
    "We could not sign into that Google account just now. Your card is still here. Please try again in a moment.",
  missingAnonymousUser:
    "We could not find the card session to connect. Please keep this page open and try again in a moment.",
  nonAnonymousUser:
    "You are already signed in, but we cannot confirm these cards were connected to this account. Your cards have not been moved.",
  uidMismatch:
    "We paused before moving forward because the account connection did not preserve this card session. Your cards have not been moved.",
  failure:
    "We could not connect Google just now. Your card is still here. Please try again in a moment.",
  sessionDiscoveryFailure:
    "We couldn't check this session's cards just now. Your current session is still here. Please try again.",
  sessionTicketFailure:
    "We couldn't prepare card recovery just now. Your current session is still here. Please try again.",
  sessionStorageFailure:
    "We couldn't save the recovery step. Your current session is still here. Please try again.",
  sessionNeedsSignIn:
    "Your cards are ready to be recovered. Choose the Google account again to continue.",
  sessionWrongAccount:
    "Please sign in to the Google account selected for card recovery to continue.",
  sessionRedemptionFailure:
    "We signed you in, but some cards are still being saved. Please try again.",
  sessionExpired:
    "Some cards were not saved before the recovery window expired. Your current session is still here.",
});

const MERGE_FAILURE_REASONS = Object.freeze({
  CREATE_TICKET_FAILED: "create-ticket-failed",
  CREDENTIAL_SIGN_IN_FAILED: "credential-sign-in-failed",
  REDEEM_TICKET_FAILED: "redeem-ticket-failed",
  CONTINUATION_FAILED: "continuation-failed",
});

const ACCOUNT_RECOVERY_STAGES = Object.freeze({
  DISCOVERY: "discovery",
  TICKET_CREATION: "ticket_creation",
  SIGN_IN: "sign_in",
  REDEMPTION: "redemption",
});

const ACCOUNT_RECOVERY_REASON_CODES = new Set([
  "network-request-failed",
  "permission-denied",
  "failed-precondition",
  "missing-anonymous-user",
  "missing-target-email",
  "missing-google-credential",
  "google-user-mismatch",
  "merge-ticket-account-mismatch",
  "ticket-expired",
  "storage-failed",
  "unknown",
]);

function recordAccountRecoveryFailure(stage, reasonCode) {
  const safeStage = Object.values(ACCOUNT_RECOVERY_STAGES).includes(stage)
    ? stage
    : "unknown";
  const safeReasonCode = ACCOUNT_RECOVERY_REASON_CODES.has(reasonCode)
    ? reasonCode
    : "unknown";

  trackMixpanelEvent("Account Recovery Failed", {
    auth_provider: "google",
    auth_step: safeStage,
    reason_code: safeReasonCode,
  });
}

function getSafeReasonCode(error) {
  const code = typeof error?.code === "string" ? error.code : "";
  const reason = typeof error?.reason === "string" ? error.reason : "";

  if (ACCOUNT_RECOVERY_REASON_CODES.has(code)) return code;
  if (ACCOUNT_RECOVERY_REASON_CODES.has(reason)) return reason;
  if (error?.status === 410) return "ticket-expired";
  return "unknown";
}

export function getSafeAuthResult(authResult) {
  if (!authResult || typeof authResult !== "object") {
    return { status: GOOGLE_AUTH_OUTCOMES.FAILURE };
  }

  const safeAuthResult = {
    status: authResult.status,
  };

  if (typeof authResult.uid === "string") {
    safeAuthResult.uid = authResult.uid;
  }

  if (typeof authResult.expectedUid === "string") {
    safeAuthResult.expectedUid = authResult.expectedUid;
  }

  if (typeof authResult.uidPreserved === "boolean") {
    safeAuthResult.uidPreserved = authResult.uidPreserved;
  }

  if (typeof authResult.errorCode === "string") {
    safeAuthResult.errorCode = authResult.errorCode;
  }

  if (typeof authResult.targetEmail === "string") {
    safeAuthResult.targetEmail = authResult.targetEmail;
  }

  if (authResult.credential && typeof authResult.credential === "object") {
    safeAuthResult.credential = authResult.credential;
  }

  return safeAuthResult;
}

function getPublicAuthResult(authResult) {
  const publicResult = { ...authResult };

  delete publicResult.credential;
  delete publicResult.targetEmail;

  return publicResult;
}

function cleanString(value) {
  return typeof value === "string" && value.trim() ? value.trim() : "";
}

function getMergeSource({ mergeContext, pendingIntent }) {
  const cardId =
    cleanString(mergeContext?.cardId) || cleanString(pendingIntent?.cardId);
  const generationId =
    cleanString(mergeContext?.generationId) ||
    cleanString(pendingIntent?.generationId);

  if (cardId) {
    return { cardId };
  }

  if (generationId) {
    return { generationId };
  }

  return null;
}

function hasCreatedMergeTicket(mergeTicket) {
  return (
    mergeTicket?.success !== false && Boolean(cleanString(mergeTicket.ticketId))
  );
}

function hasRedeemedMergeTicket(mergeResult) {
  return mergeResult?.success === true;
}

function getMergeFailureMessage() {
  return ACCOUNT_LINKING_STATUS_MESSAGES.mergeFailure;
}

function resultOutcome(outcome, statusMessage = "") {
  return {
    outcome,
    statusMessage,
  };
}

function recoveryErrorOutcome(
  statusMessage,
  recoveryAction = "",
  recoveryState = null
) {
  return {
    ...errorOutcome(statusMessage),
    ...(recoveryAction ? { recoveryAction } : {}),
    ...(recoveryState ? { recoveryState } : {}),
  };
}

function errorOutcome(statusMessage) {
  return resultOutcome(ACCOUNT_LINKING_RESULT_OUTCOMES.ERROR, statusMessage);
}

function fallbackConflictOutcome(statusMessage) {
  return resultOutcome(
    ACCOUNT_LINKING_RESULT_OUTCOMES.FALLBACK_CONFLICT,
    statusMessage
  );
}

async function signInWithConflictCredential(credential) {
  let signInResult;

  try {
    signInResult = getSafeAuthResult(
      await signInWithGoogleCredential(credential)
    );
  } catch (error) {
    return {
      outcome: errorOutcome(
        getMergeFailureMessage(
          MERGE_FAILURE_REASONS.CREDENTIAL_SIGN_IN_FAILED,
          error
        )
      ),
    };
  }

  if (signInResult.status !== GOOGLE_AUTH_OUTCOMES.SUCCESS) {
    return {
      outcome: errorOutcome(
        getMergeFailureMessage(
          MERGE_FAILURE_REASONS.CREDENTIAL_SIGN_IN_FAILED,
          signInResult
        )
      ),
    };
  }

  return { signInResult };
}

async function continueWithLinkedUser({
  mergeResult,
  onLinked,
  pendingIntent,
  signInResult,
  isSessionSave = false,
}) {
  if (typeof onLinked !== "function") {
    return errorOutcome(
      getMergeFailureMessage(MERGE_FAILURE_REASONS.CONTINUATION_FAILED)
    );
  }

  try {
    await onLinked({
      authResult: signInResult,
      pendingIntent,
      ...(mergeResult ? { mergeResult } : {}),
    });
    if (isSessionSave) {
      clearAccountMenuIntent();
    } else {
      clearPendingAuthIntent();
    }
    return resultOutcome(ACCOUNT_LINKING_RESULT_OUTCOMES.LINKED);
  } catch (error) {
    return errorOutcome(
      getMergeFailureMessage(
        MERGE_FAILURE_REASONS.CONTINUATION_FAILED,
        error
      )
    );
  }
}

function getSessionPendingIntent(sessionIntent, recoveryState = null) {
  const source = sessionIntent || recoveryState || {};

  return {
    action: "save-session",
    returnTo: source.returnTo || "/refine",
    ...(source.draft ? { draft: source.draft } : {}),
  };
}

function buildRecoveryState({
  sourceUid,
  tickets,
  expiresAt,
  sessionIntent,
  destinationUid = "",
}) {
  return {
    sourceUid,
    tickets,
    expiresAt,
    createdAt: Date.now(),
    ...(destinationUid ? { destinationUid } : {}),
    ...(sessionIntent?.returnTo ? { returnTo: sessionIntent.returnTo } : {}),
    ...(sessionIntent?.draft ? { draft: sessionIntent.draft } : {}),
  };
}

async function discoverSessionGenerations(sourceUid) {
  const settledUser = await waitForSettledAuthUser({
    contextUser: auth.currentUser,
    expectedUid: sourceUid,
  });

  if (!settledUser || settledUser.uid !== sourceUid || !settledUser.isAnonymous) {
    const error = new Error("Anonymous session required");
    error.code = "missing-anonymous-user";
    throw error;
  }

  return getAllOwnedCardGenerations(sourceUid);
}

async function createSessionTickets({
  sourceUid,
  targetEmail,
  generations,
  sessionIntent,
}) {
  const generationIds = Array.from(
    new Set(
      generations
        .map((generation) => cleanString(generation?.id))
        .filter(Boolean)
    )
  );
  let recoveryState = getAccountSessionRecoveryState();

  if (recoveryState && recoveryState.sourceUid !== sourceUid) {
    recoveryState = null;
  }

  const existingTickets = new Map(
    (recoveryState?.tickets || []).map((ticket) => [
      ticket.generationId,
      ticket,
    ])
  );
  const tickets = [];
  const expiresAt =
    recoveryState?.expiresAt || Date.now() + ACCOUNT_SESSION_RECOVERY_TTL_MS;

  for (const generationId of generationIds) {
    const existingTicket = existingTickets.get(generationId);

    if (existingTicket) {
      tickets.push(existingTicket);
      continue;
    }

    const ticketResponse = await createLettuceTalkMergeTicket({
      targetEmail,
      generationId,
    });
    const ticketId = cleanString(ticketResponse?.ticketId);

    if (!ticketId) {
      const error = new Error("Merge ticket was not created");
      error.code = "invalid-create-merge-ticket-response";
      throw error;
    }

    tickets.push({
      ticketId,
      generationId,
      status: "pending",
    });

    const nextState = buildRecoveryState({
      sourceUid,
      tickets,
      expiresAt,
      sessionIntent,
    });

    if (!saveAccountSessionRecoveryState(nextState)) {
      const error = new Error("Recovery state could not be saved");
      error.code = "storage-failed";
      throw error;
    }
    recoveryState = nextState;
  }

  const finalState = buildRecoveryState({
    sourceUid,
    tickets,
    expiresAt,
    sessionIntent,
  });

  if (!saveAccountSessionRecoveryState(finalState)) {
    const error = new Error("Recovery state could not be saved");
    error.code = "storage-failed";
    throw error;
  }

  return finalState;
}

function isExpiredTicketError(error) {
  return (
    error?.status === 410 ||
    error?.code === "ticket-expired" ||
    error?.reason === "ticket-expired"
  );
}

function isMergeTicketAccountMismatchError(error) {
  return (
    error?.code === "merge-ticket-account-mismatch" ||
    error?.reason === "merge-ticket-account-mismatch"
  );
}

function removeUnverifiedAnonymousDestination(recoveryState) {
  const currentUser = auth.currentUser;

  if (
    recoveryState?.destinationUidVerified === true ||
    !recoveryState?.destinationUid ||
    currentUser?.isAnonymous !== true ||
    currentUser.uid !== recoveryState.destinationUid
  ) {
    return { recoveryState, storageFailed: false };
  }

  const nextState = { ...recoveryState };
  delete nextState.destinationUid;
  nextState.destinationUidVerified = false;

  if (!saveAccountSessionRecoveryState(nextState)) {
    return { recoveryState, storageFailed: true };
  }

  return { recoveryState: nextState, storageFailed: false };
}

export async function resumeAccountSessionRecovery({
  onLinked,
  expectedUid = "",
} = {}) {
  let recoveryState = getAccountSessionRecoveryState();

  if (!recoveryState) {
    return resultOutcome(ACCOUNT_LINKING_RESULT_OUTCOMES.LINKED);
  }

  if (!hasUnfinishedAccountSessionRecovery(recoveryState)) {
    clearAccountSessionRecoveryState();
    return resultOutcome(ACCOUNT_LINKING_RESULT_OUTCOMES.LINKED);
  }

  if (
    recoveryState.tickets.every((ticket) => ticket.status === "expired")
  ) {
    recordAccountRecoveryFailure(
      ACCOUNT_RECOVERY_STAGES.REDEMPTION,
      "ticket-expired"
    );
    return recoveryErrorOutcome(
      ACCOUNT_LINKING_STATUS_MESSAGES.sessionExpired,
      "expired",
      recoveryState
    );
  }

  const anonymousDestinationResult =
    removeUnverifiedAnonymousDestination(recoveryState);

  if (anonymousDestinationResult.storageFailed) {
    recordAccountRecoveryFailure(
      ACCOUNT_RECOVERY_STAGES.SIGN_IN,
      "storage-failed"
    );
    return recoveryErrorOutcome(
      ACCOUNT_LINKING_STATUS_MESSAGES.sessionStorageFailure,
      "retry",
      recoveryState
    );
  }

  recoveryState = anonymousDestinationResult.recoveryState;

  const requestedUid = cleanString(expectedUid);
  if (!recoveryState.destinationUid && !requestedUid) {
    recordAccountRecoveryFailure(
      ACCOUNT_RECOVERY_STAGES.SIGN_IN,
      "missing-google-credential"
    );
    return recoveryErrorOutcome(
      ACCOUNT_LINKING_STATUS_MESSAGES.sessionNeedsSignIn,
      "sign-in",
      recoveryState
    );
  }

  const destinationUid = recoveryState.destinationUid || requestedUid;
  const settledUser = await waitForSettledAuthUser({
    contextUser: auth.currentUser,
    expectedUid: destinationUid,
    requirePermanentUser: true,
  });

  if (!settledUser) {
    recordAccountRecoveryFailure(
      ACCOUNT_RECOVERY_STAGES.SIGN_IN,
      destinationUid ? "google-user-mismatch" : "missing-google-credential"
    );
    return recoveryErrorOutcome(
      destinationUid
        ? ACCOUNT_LINKING_STATUS_MESSAGES.sessionWrongAccount
        : ACCOUNT_LINKING_STATUS_MESSAGES.sessionNeedsSignIn,
      "sign-in",
      recoveryState
    );
  }

  if (!recoveryState.destinationUid) {
    recoveryState = {
      ...recoveryState,
      destinationUid: settledUser.uid,
      destinationUidVerified: false,
    };

    if (!saveAccountSessionRecoveryState(recoveryState)) {
      recordAccountRecoveryFailure(
        ACCOUNT_RECOVERY_STAGES.REDEMPTION,
        "storage-failed"
      );
      return recoveryErrorOutcome(
        ACCOUNT_LINKING_STATUS_MESSAGES.sessionStorageFailure,
        "retry",
        recoveryState
      );
    }
  }

  for (const ticket of recoveryState.tickets) {
    if (ticket.status !== "pending") continue;

    try {
      await redeemLettuceTalkMergeTicket({
        ticketId: ticket.ticketId,
        expectedUid: recoveryState.destinationUid,
      });
    } catch (error) {
      if (isExpiredTicketError(error)) {
        const expiredState = {
          ...recoveryState,
          tickets: recoveryState.tickets.map((candidate) =>
            candidate.ticketId === ticket.ticketId
              ? { ...candidate, status: "expired" }
              : candidate
          ),
        };

        if (saveAccountSessionRecoveryState(expiredState)) {
          recoveryState = expiredState;
        }
        recordAccountRecoveryFailure(
          ACCOUNT_RECOVERY_STAGES.REDEMPTION,
          "ticket-expired"
        );
        return recoveryErrorOutcome(
          ACCOUNT_LINKING_STATUS_MESSAGES.sessionExpired,
          "expired",
          recoveryState
        );
      }

      if (isMergeTicketAccountMismatchError(error)) {
        let mismatchState = recoveryState;

        if (recoveryState.destinationUidVerified !== true) {
          mismatchState = { ...recoveryState };
          delete mismatchState.destinationUid;
          mismatchState.destinationUidVerified = false;

          if (!saveAccountSessionRecoveryState(mismatchState)) {
            recordAccountRecoveryFailure(
              ACCOUNT_RECOVERY_STAGES.REDEMPTION,
              "storage-failed"
            );
            return recoveryErrorOutcome(
              ACCOUNT_LINKING_STATUS_MESSAGES.sessionStorageFailure,
              "retry",
              recoveryState
            );
          }

          recoveryState = mismatchState;
        }

        recordAccountRecoveryFailure(
          ACCOUNT_RECOVERY_STAGES.REDEMPTION,
          "merge-ticket-account-mismatch"
        );
        return recoveryErrorOutcome(
          ACCOUNT_LINKING_STATUS_MESSAGES.sessionWrongAccount,
          "sign-in",
          recoveryState
        );
      }

      recordAccountRecoveryFailure(
        ACCOUNT_RECOVERY_STAGES.REDEMPTION,
        getSafeReasonCode(error)
      );
      return recoveryErrorOutcome(
        ACCOUNT_LINKING_STATUS_MESSAGES.sessionRedemptionFailure,
        "retry",
        recoveryState
      );
    }

    const redeemedState = {
      ...recoveryState,
      destinationUidVerified: true,
      tickets: recoveryState.tickets.map((candidate) =>
        candidate.ticketId === ticket.ticketId
          ? { ...candidate, status: "redeemed" }
          : candidate
      ),
    };

    if (!saveAccountSessionRecoveryState(redeemedState)) {
      recordAccountRecoveryFailure(
        ACCOUNT_RECOVERY_STAGES.REDEMPTION,
        "storage-failed"
      );
      return recoveryErrorOutcome(
        ACCOUNT_LINKING_STATUS_MESSAGES.sessionStorageFailure,
        "retry",
        recoveryState
      );
    }
    recoveryState = redeemedState;
  }

  if (typeof onLinked !== "function") {
    return recoveryErrorOutcome(
      ACCOUNT_LINKING_STATUS_MESSAGES.continuationFailure,
      "retry",
      recoveryState
    );
  }

  try {
    await onLinked({
      authResult: {
        status: GOOGLE_AUTH_OUTCOMES.SUCCESS,
        uid: recoveryState.destinationUid,
      },
      pendingIntent: getSessionPendingIntent(null, recoveryState),
      recoveryState,
    });
  } catch {
    return recoveryErrorOutcome(
      ACCOUNT_LINKING_STATUS_MESSAGES.continuationFailure,
      "retry",
      recoveryState
    );
  }

  clearAccountSessionRecoveryState();
  clearAccountMenuIntent();
  return resultOutcome(ACCOUNT_LINKING_RESULT_OUTCOMES.LINKED);
}

async function handleSessionSaveConflict({
  authResult,
  sessionIntent,
  onLinked,
}) {
  const sourceUid = cleanString(sessionIntent?.sourceUid);
  const targetEmail = cleanString(authResult?.targetEmail);
  const credential = authResult?.credential || null;

  if (!sourceUid) {
    recordAccountRecoveryFailure(
      ACCOUNT_RECOVERY_STAGES.DISCOVERY,
      "missing-anonymous-user"
    );
    return recoveryErrorOutcome(
      ACCOUNT_LINKING_STATUS_MESSAGES.sessionDiscoveryFailure,
      "retry"
    );
  }

  if (!credential || !targetEmail) {
    recordAccountRecoveryFailure(
      ACCOUNT_RECOVERY_STAGES.SIGN_IN,
      credential ? "missing-target-email" : "missing-google-credential"
    );
    return recoveryErrorOutcome(
      ACCOUNT_LINKING_STATUS_MESSAGES.sessionNeedsSignIn,
      "sign-in"
    );
  }

  let generations;

  try {
    generations = await discoverSessionGenerations(sourceUid);
  } catch (error) {
    recordAccountRecoveryFailure(
      ACCOUNT_RECOVERY_STAGES.DISCOVERY,
      getSafeReasonCode(error)
    );
    return recoveryErrorOutcome(
      ACCOUNT_LINKING_STATUS_MESSAGES.sessionDiscoveryFailure,
      "retry"
    );
  }

  if (generations.length === 0) {
    const credentialSignIn = await signInWithConflictCredential(credential);

    if (credentialSignIn.outcome) {
      recordAccountRecoveryFailure(
        ACCOUNT_RECOVERY_STAGES.SIGN_IN,
        "network-request-failed"
      );
      return recoveryErrorOutcome(
        ACCOUNT_LINKING_STATUS_MESSAGES.sessionNeedsSignIn,
        "sign-in"
      );
    }

    return continueWithLinkedUser({
      signInResult: credentialSignIn.signInResult,
      onLinked,
      pendingIntent: sessionIntent,
      isSessionSave: true,
    });
  }

  let recoveryState;

  try {
    recoveryState = await createSessionTickets({
      sourceUid,
      targetEmail,
      generations,
      sessionIntent,
    });
  } catch (error) {
    const reasonCode = getSafeReasonCode(error);
    const stage = reasonCode === "storage-failed"
      ? ACCOUNT_RECOVERY_STAGES.TICKET_CREATION
      : ACCOUNT_RECOVERY_STAGES.TICKET_CREATION;
    recordAccountRecoveryFailure(stage, reasonCode);
    return recoveryErrorOutcome(
      reasonCode === "storage-failed"
        ? ACCOUNT_LINKING_STATUS_MESSAGES.sessionStorageFailure
        : ACCOUNT_LINKING_STATUS_MESSAGES.sessionTicketFailure,
      "retry",
      getAccountSessionRecoveryState()
    );
  }

  const credentialSignIn = await signInWithConflictCredential(credential);

  if (credentialSignIn.outcome) {
    recordAccountRecoveryFailure(
      ACCOUNT_RECOVERY_STAGES.SIGN_IN,
      "network-request-failed"
    );
    return recoveryErrorOutcome(
      ACCOUNT_LINKING_STATUS_MESSAGES.sessionNeedsSignIn,
      "sign-in",
      recoveryState
    );
  }

  const destinationUid = cleanString(credentialSignIn.signInResult.uid);

  if (!destinationUid) {
    recordAccountRecoveryFailure(
      ACCOUNT_RECOVERY_STAGES.SIGN_IN,
      "missing-google-credential"
    );
    return recoveryErrorOutcome(
      ACCOUNT_LINKING_STATUS_MESSAGES.sessionNeedsSignIn,
      "sign-in",
      recoveryState
    );
  }

  return resumeAccountSessionRecovery({
    onLinked,
    expectedUid: destinationUid,
  });
}

export async function handleGoogleAccountLinkingResult({
  authResult,
  mergeContext,
  onLinked,
  onConflict,
  sessionIntent,
  sessionSave = false,
  recoveryOnly = false,
  expectedUid = "",
} = {}) {
  if (recoveryOnly) {
    return resumeAccountSessionRecovery({
      onLinked,
      expectedUid:
        cleanString(expectedUid) ||
        cleanString(authResult?.uid) ||
        cleanString(sessionIntent?.destinationUid),
    });
  }

  const safeAuthResult = getSafeAuthResult(authResult);

  if (safeAuthResult.status === GOOGLE_AUTH_OUTCOMES.SUCCESS) {
    const pendingIntent = sessionSave
      ? sessionIntent || getSessionPendingIntent()
      : getPendingAuthIntent();

    if (typeof onLinked !== "function") {
      return errorOutcome(
        ACCOUNT_LINKING_STATUS_MESSAGES.continuationFailure
      );
    }

    try {
      await onLinked({
        authResult: safeAuthResult,
        pendingIntent,
      });
      if (sessionSave) {
        clearAccountMenuIntent();
      } else {
        clearPendingAuthIntent();
      }
      return resultOutcome(ACCOUNT_LINKING_RESULT_OUTCOMES.LINKED);
    } catch {
      return errorOutcome(
        ACCOUNT_LINKING_STATUS_MESSAGES.continuationFailure
      );
    }
  }

  if (safeAuthResult.status === GOOGLE_AUTH_OUTCOMES.CANCELLED) {
    return resultOutcome(ACCOUNT_LINKING_RESULT_OUTCOMES.CANCELLED);
  }

  if (safeAuthResult.status === GOOGLE_AUTH_OUTCOMES.CONFLICT) {
    try {
      await onConflict?.(getPublicAuthResult(safeAuthResult));
    } catch {
      // The future conflict flow should not break this sheet state.
    }

    if (sessionSave) {
      return handleSessionSaveConflict({
        authResult: safeAuthResult,
        sessionIntent,
        onLinked,
      });
    }

    const targetEmail = cleanString(safeAuthResult.targetEmail);
    const credential = safeAuthResult.credential || null;
    const pendingIntent = getPendingAuthIntent();
    const mergeSource = getMergeSource({ mergeContext, pendingIntent });
    const canSignInWithoutMerge =
      !mergeSource &&
      pendingIntent?.action === PENDING_AUTH_INTENT_ACTIONS.CREATE_ANOTHER;

    if (!mergeSource && !canSignInWithoutMerge) {
      return fallbackConflictOutcome(
        ACCOUNT_LINKING_STATUS_MESSAGES.missingMergeSource
      );
    }

    if (!credential || (!targetEmail && !canSignInWithoutMerge)) {
      return fallbackConflictOutcome(
        ACCOUNT_LINKING_STATUS_MESSAGES.missingMergeTarget
      );
    }

    if (!mergeSource) {
      const credentialSignIn = await signInWithConflictCredential(credential);

      if (credentialSignIn.outcome) {
        return credentialSignIn.outcome;
      }

      return continueWithLinkedUser({
        signInResult: credentialSignIn.signInResult,
        onLinked,
        pendingIntent,
      });
    }

    let mergeTicket;

    try {
      mergeTicket = await createLettuceTalkMergeTicket({
        targetEmail,
        ...mergeSource,
      });
    } catch (error) {
      return errorOutcome(
        getMergeFailureMessage(
          MERGE_FAILURE_REASONS.CREATE_TICKET_FAILED,
          error
        )
      );
    }

    if (!hasCreatedMergeTicket(mergeTicket)) {
      return errorOutcome(
        getMergeFailureMessage(
          MERGE_FAILURE_REASONS.CREATE_TICKET_FAILED,
          {
            code: "invalid-create-merge-ticket-response",
          }
        )
      );
    }

    const credentialSignIn = await signInWithConflictCredential(credential);

    if (credentialSignIn.outcome) {
      return credentialSignIn.outcome;
    }

    const { signInResult } = credentialSignIn;
    let mergeResult;

    try {
      mergeResult = await redeemLettuceTalkMergeTicket({
        ticketId: mergeTicket.ticketId,
        expectedUid: signInResult.uid,
      });
    } catch (error) {
      return errorOutcome(
        getMergeFailureMessage(
          MERGE_FAILURE_REASONS.REDEEM_TICKET_FAILED,
          error
        )
      );
    }

    if (!hasRedeemedMergeTicket(mergeResult)) {
      return errorOutcome(
        getMergeFailureMessage(
          MERGE_FAILURE_REASONS.REDEEM_TICKET_FAILED,
          {
            code: "invalid-redeem-merge-ticket-response",
          }
        )
      );
    }

    return continueWithLinkedUser({
      signInResult,
      pendingIntent,
      mergeResult,
      onLinked,
    });
  }

  if (safeAuthResult.status === GOOGLE_AUTH_OUTCOMES.MISSING_ANONYMOUS_USER) {
    return errorOutcome(
      ACCOUNT_LINKING_STATUS_MESSAGES.missingAnonymousUser
    );
  }

  if (safeAuthResult.status === GOOGLE_AUTH_OUTCOMES.NON_ANONYMOUS_USER) {
    return errorOutcome(
      ACCOUNT_LINKING_STATUS_MESSAGES.nonAnonymousUser
    );
  }

  if (safeAuthResult.status === GOOGLE_AUTH_OUTCOMES.UID_MISMATCH) {
    return errorOutcome(ACCOUNT_LINKING_STATUS_MESSAGES.uidMismatch);
  }

  if (safeAuthResult.status === GOOGLE_AUTH_OUTCOMES.FAILURE) {
    return errorOutcome(ACCOUNT_LINKING_STATUS_MESSAGES.failure);
  }

  return errorOutcome(ACCOUNT_LINKING_STATUS_MESSAGES.failure);
}
